'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ExternalLink, InfoIcon } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const googlePayFormSchema = z.object({
  merchantName: z.string().min(1, 'Merchant name is required'),
  merchantId: z.string().min(1, 'Merchant ID is required'),
  sandboxed: z.boolean(),
  enableExpressCheckout: z.boolean(),
  enablePaymentFormCheckout: z.boolean(),
})

type FormValues = z.infer<typeof googlePayFormSchema>

interface GooglePayFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function GooglePayForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: GooglePayFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(googlePayFormSchema),
    defaultValues: {
      merchantName: defaultValues.metadata?.merchantName ?? '',
      merchantId: defaultValues.metadata?.merchantId ?? '',
      sandboxed: defaultValues.metadata?.sandboxed ?? false,
      enableExpressCheckout: defaultValues.metadata?.enableExpressCheckout ?? true,
      enablePaymentFormCheckout: defaultValues.metadata?.enablePaymentFormCheckout ?? false,
    },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { merchantName, merchantId, sandboxed, enableExpressCheckout, enablePaymentFormCheckout } = data

    await onSubmit({
      metadata: {
        merchantName,
        merchantId,
        sandboxed,
        enableExpressCheckout,
        enablePaymentFormCheckout,
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">{/* Setup Requirements */}
      <Alert className="border-blue-200 bg-blue-50">
        <InfoIcon className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">{t('googlePay.setupRequirements')}</AlertTitle>
        <AlertDescription className="text-blue-800">
          <p className="mb-2">{t('googlePay.setupRequirementsDescription')}</p>
          <ol className="ml-2 list-inside list-decimal space-y-1">
            <li>
              {t('googlePay.setupStep1')}{' '}
              <a
                href="https://pay.google.com/business/console/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium underline"
              >
                {t('googlePay.setupStep1Link')}
                <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>{t('googlePay.setupStep2')}</li>
            <li>{t('googlePay.setupStep3')}</li>
          </ol>
        </AlertDescription>
      </Alert>

      {/* Merchant Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('googlePay.merchantCredentials')}</CardTitle>
          <CardDescription>
            {t('googlePay.merchantCredentialsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="merchantName">
              {t('googlePay.merchantName')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="merchantName"
              placeholder={t('googlePay.merchantNamePlaceholder')}
              disabled={isLoading}
              {...register('merchantName')}
            />
            {errors.merchantName && (
              <p className="text-xs text-destructive">{errors.merchantName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="merchantId">
              {t('googlePay.merchantId')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="merchantId"
              placeholder="BCR2DN6TSXXXZ000"
              disabled={isLoading}
              {...register('merchantId')}
            />
            <p className="text-xs text-muted-foreground">
              {t('googlePay.merchantIdHint')}
            </p>
            {errors.merchantId && (
              <p className="text-xs text-destructive">{errors.merchantId.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Environment Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('googlePay.environmentSettings')}</CardTitle>
          <CardDescription>
            {t('googlePay.environmentDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sandboxed" className="font-medium">{t('googlePay.useSandbox')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('googlePay.sandboxHint')}
              </p>
            </div>
            <Switch
              id="sandboxed"
              checked={watch('sandboxed')}
              onCheckedChange={(checked) => setValue('sandboxed', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Google Pay Features */}
      <Card>
        <CardHeader>
          <CardTitle>{t('googlePay.features')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="enableExpressCheckout"
              checked={watch('enableExpressCheckout')}
              onCheckedChange={(checked) => setValue('enableExpressCheckout', checked as boolean)}
              disabled={isLoading}
            />
            <div className="flex-1 space-y-1">
              <Label htmlFor="enableExpressCheckout" className="cursor-pointer">
                {t('googlePay.enableExpressCheckout')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('googlePay.expressCheckoutHint')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="enablePaymentFormCheckout"
              checked={watch('enablePaymentFormCheckout')}
              onCheckedChange={(checked) => setValue('enablePaymentFormCheckout', checked as boolean)}
              disabled={isLoading}
            />
            <div className="flex-1 space-y-1">
              <Label htmlFor="enablePaymentFormCheckout" className="cursor-pointer">
                {t('googlePay.enablePaymentFormCheckout')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('googlePay.paymentFormCheckoutHint')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Note */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertDescription className="text-sm text-amber-700">
          <span dangerouslySetInnerHTML={{ __html: t('googlePay.configNote') }} />
        </AlertDescription>
      </Alert>
    </form>
  )
}
